import { Fragment } from 'react'
import {
    type APIOrganization,
    type APIQueueTaskKind,
    DOCUMENT_QUEUE_TASK_KINDS,
} from '@visorpro/client'
import _ from 'lodash'
import { Link } from 'react-router-dom'
import { Box, Chip, chipClasses, Typography } from '@mui/material'
import type { DictionaryArray } from '../types'

const formatMetadataValue = (value: unknown): string => {
    if (typeof value === 'string') return value
    if (value === null || value === undefined) return ''
    return JSON.stringify(value)
}

interface MetadataEntriesProps {
    kind: APIQueueTaskKind
    metadata: object
    organizations?: DictionaryArray<APIOrganization>
}

// The record that a metadata id refers to, shown as a pill in place of
// the id.
interface MetadataReference {
    label: string
    name: string
    to: string
}

/**
 * What a task works on, as the rows of a tooltip: the entries of its
 * metadata, with the records that they refer to shown by name.
 */
export const MetadataEntries = ({
    kind,
    metadata,
    organizations,
}: MetadataEntriesProps) => {
    const {
        document_id: documentId,
        title,
        organization_id: organizationId,
    } = metadata as Record<string, unknown>

    // An id is shown by name only once its record's name is known, and
    // stays a plain id otherwise.
    const references: Record<string, MetadataReference | undefined> = {}

    // The API fills in the title of a document task's document.
    if (
        DOCUMENT_QUEUE_TASK_KINDS.includes(kind) &&
        typeof documentId === 'string' &&
        documentId !== '' &&
        typeof title === 'string' &&
        title !== ''
    ) {
        references.document_id = {
            label: 'Document',
            name: title,
            to: `/documents/${documentId}`,
        }
    }

    const organization: APIOrganization | undefined =
        typeof organizationId === 'string'
            ? organizations?.get(organizationId)
            : undefined
    if (organization) {
        references.organization_id = {
            label: 'Organization',
            name: organization.name,
            to: `/organizations/${organization.id}`,
        }
    }

    // The document's pill is made of its id and its title, so the title
    // has no row of its own.
    const entries = Object.entries(metadata).filter(
        ([key]) => !(references.document_id && key === 'title'),
    )

    return (
        <Box
            component="dl"
            sx={{
                display: 'grid',
                gridTemplateColumns: 'max-content minmax(0, 1fr)',
                columnGap: 2,
                rowGap: 0.25,
                alignItems: 'baseline',
                m: 0,
            }}>
            {entries.map(([key, value]) => {
                const reference = references[key]

                return (
                    <Fragment key={key}>
                        <Typography
                            variant="caption"
                            component="dt"
                            color="text.secondary"
                            sx={{ whiteSpace: 'nowrap' }}>
                            {reference?.label ?? _.startCase(key)}
                        </Typography>
                        <Typography
                            variant="caption"
                            component="dd"
                            sx={{ m: 0, wordBreak: 'break-all' }}>
                            {reference ? (
                                <Link to={reference.to}>
                                    <Chip
                                        label={reference.name}
                                        title={reference.name}
                                        size="small"
                                        clickable
                                        // As tall as a line of text, and
                                        // sitting on its baseline, so the
                                        // row lines up with the text rows.
                                        // Its text is as small as the
                                        // text around it, rather than of
                                        // the size that the theme gives
                                        // to the label of a chip.
                                        sx={{
                                            height: 20,
                                            verticalAlign: 'baseline',
                                            fontSize: 'inherit',
                                            [`& .${chipClasses.label}`]: {
                                                fontSize: 'inherit',
                                            },
                                        }}
                                    />
                                </Link>
                            ) : (
                                formatMetadataValue(value)
                            )}
                        </Typography>
                    </Fragment>
                )
            })}
        </Box>
    )
}
